/**
 * Lesson Player — Loader + Sequencer
 *
 * loadLesson(id) — fetches LessonDefinition from /api/v1/lessons/:id
 * startSequencer(lesson) — interval-based event sequencer (30 FPS)
 * Fires CONTROL/AUTO events directly; registers graded events as PendingGrade objects.
 * scoringEngine.ts calls checkPendingGrade() when the user acts.
 */

import { LessonEventType, LessonFlowState } from '@graphicsim/shared-types';
import type { LessonDefinition, TimestampEvent, EventResult } from '@graphicsim/shared-types';
import { TIMING, SCORING } from '@graphicsim/shared-constants';
import { lessonStore } from '../stores/lessonStore';
import { eventBus } from './eventBus';

// --- Pending Grade ---

export interface PendingGrade {
  eventIndex: number;
  event: TimestampEvent;
  firedAt: number;       // wall-clock ms when this event became active
  deadline: number;      // wall-clock ms when this event expires (firedAt + tolerance)
  resolved: boolean;
}

// --- Module State ---

let lessonCache = new Map<string, LessonDefinition>();
let currentLesson: LessonDefinition | null = null;
let sequencerInterval: ReturnType<typeof setInterval> | null = null;
let sequencerStartTime = 0;
let nextEventIndex = 0;
let pendingGrades: PendingGrade[] = [];
let eventResults: EventResult[] = [];
let instructionCallback: ((text: string) => void) | null = null;

// --- Public API ---

/** Fetch lesson JSON from server (cached) */
export async function loadLesson(id: string): Promise<LessonDefinition> {
  if (lessonCache.has(id)) return lessonCache.get(id)!;

  const res = await fetch(`/api/v1/lessons/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`Failed to load lesson ${id}: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? 'Unknown error');

  const lesson = json.data as LessonDefinition;
  lessonCache.set(id, lesson);
  return lesson;
}

/** Register a callback for CONTROL show_instruction events */
export function onInstruction(cb: (text: string) => void): void {
  instructionCallback = cb;
}

/** Start the event sequencer for a loaded lesson */
export function startSequencer(lesson: LessonDefinition): void {
  stopSequencer();
  currentLesson = lesson;
  nextEventIndex = 0;
  pendingGrades = [];
  eventResults = [];
  sequencerStartTime = Date.now();

  const intervalMs = Math.round(1000 / TIMING.TARGET_FPS); // ~33ms

  sequencerInterval = setInterval(() => {
    const elapsed = Date.now() - sequencerStartTime;
    processEvents(elapsed);
    expirePendingGrades();
  }, intervalMs);
}

/** Stop the sequencer and clean up */
export function stopSequencer(): void {
  if (sequencerInterval !== null) {
    clearInterval(sequencerInterval);
    sequencerInterval = null;
  }
  currentLesson = null;
  nextEventIndex = 0;
  pendingGrades = [];
}

/** Get all event results (for results screen) */
export function getEventResults(): EventResult[] {
  return [...eventResults];
}

/**
 * Called by scoringEngine when the user performs a graded action.
 * Returns points earned (0 if no matching pending grade).
 */
export function checkPendingGrade(
  type: LessonEventType,
  action: string,
  value: string,
): number {
  const now = Date.now();

  for (const pg of pendingGrades) {
    if (pg.resolved) continue;
    if (pg.event.type !== type) continue;

    // Match by target or expectedValue
    const matchTarget = !pg.event.target || pg.event.target === value;
    const matchExpected = !pg.event.expectedValue || pg.event.expectedValue === value;
    if (!matchTarget && !matchExpected) continue;

    // Found a match — calculate score
    pg.resolved = true;
    const lateness = Math.max(0, now - pg.firedAt);
    const points = calculatePoints(pg.event.points, lateness);

    // Record result
    eventResults.push({
      eventIndex: pg.eventIndex,
      type: pg.event.type,
      expected: pg.event.expectedValue ?? pg.event.target ?? '',
      actual: value,
      pointsEarned: points,
      pointsPossible: pg.event.points,
    });

    // Update store
    if (points > 0) {
      lessonStore.getState().addScore(points);
    }

    return points;
  }

  return 0;
}

// --- Internal ---

function processEvents(elapsed: number): void {
  if (!currentLesson) return;
  const timestamps = currentLesson.timestamps;

  while (nextEventIndex < timestamps.length) {
    const evt = timestamps[nextEventIndex];
    if (evt.time > elapsed) break; // not yet time

    fireEvent(evt, nextEventIndex);
    nextEventIndex++;
  }

  // If all events processed and no pending grades remain, end lesson
  if (nextEventIndex >= timestamps.length && pendingGrades.every((pg) => pg.resolved)) {
    // Check if the last event was auto:end_lesson — that already ended it
    const lastEvt = timestamps[timestamps.length - 1];
    if (lastEvt?.type === LessonEventType.AUTO && lastEvt?.action === 'end_lesson') {
      return; // already handled
    }
  }
}

function fireEvent(evt: TimestampEvent, index: number): void {
  switch (evt.type) {
    case LessonEventType.CONTROL:
      handleControlEvent(evt);
      break;

    case LessonEventType.AUTO:
      handleAutoEvent(evt);
      break;

    // Graded events — register as pending
    case LessonEventType.NAVIGATE:
    case LessonEventType.TEMPLATE:
    case LessonEventType.EDIT:
    case LessonEventType.SHOT:
    case LessonEventType.DATA:
    case LessonEventType.PROGRAM: {
      const tolerance = evt.tolerance ?? SCORING.TOLERANCE_MS;
      const now = Date.now();
      pendingGrades.push({
        eventIndex: index,
        event: evt,
        firedAt: now,
        deadline: now + tolerance,
        resolved: false,
      });
      // Show instruction if event has a description
      if (evt.description && instructionCallback) {
        instructionCallback(evt.description);
      }
      break;
    }
  }
}

function handleControlEvent(evt: TimestampEvent): void {
  if (evt.action === 'show_instruction' && evt.description) {
    if (instructionCallback) {
      instructionCallback(evt.description);
    }
    // Also emit on event bus for other listeners
    eventBus.emit('ui:panelToggled', { panel: 'preview', visible: true });
  }
}

function handleAutoEvent(evt: TimestampEvent): void {
  if (evt.action === 'end_lesson') {
    stopSequencer();
    lessonStore.getState().endLesson();
  }
}

function expirePendingGrades(): void {
  const now = Date.now();
  for (const pg of pendingGrades) {
    if (pg.resolved) continue;
    if (now >= pg.deadline) {
      pg.resolved = true;
      // Record as missed (0 points)
      eventResults.push({
        eventIndex: pg.eventIndex,
        type: pg.event.type,
        expected: pg.event.expectedValue ?? pg.event.target ?? '',
        actual: '(missed)',
        pointsEarned: 0,
        pointsPossible: pg.event.points,
      });
    }
  }
}

function calculatePoints(basePoints: number, latenessMs: number): number {
  if (latenessMs <= SCORING.TOLERANCE_MS) return basePoints;

  const lateMs = latenessMs - SCORING.TOLERANCE_MS;
  const penalty = Math.floor(lateMs / 500) * SCORING.PENALTY_PER_500MS;
  const maxPenalty = basePoints * (SCORING.MAX_PENALTY_PERCENT / 100);
  const actualPenalty = Math.min(penalty, maxPenalty);

  return Math.max(0, basePoints - actualPenalty);
}
